import dayjs from "dayjs";
import HealthFormAnswer from "../../../requests/member/health/HealthFormAnswer";
import {
    EMPTY_SCHEMA,
    NON_ANSWER_FIELD_TYPES,
    QuestionnaireField,
    QuestionnaireSchema,
    QuestionnaireSection,
} from "./QuestionnaireSchema";

// Tolerant parse of a raw client_setting JSON string into a QuestionnaireSchema.
// The Forms panel lets operators save arbitrary JSON, so a syntactically-valid
// blob may still be structurally invalid. We enforce a safe minimum shape and
// drop anything malformed, so DynamicQuestionnaire (section.fields.map) and
// schemaToAnswers can never crash on load. A blob with no usable sections falls
// back to EMPTY_SCHEMA (the caller then substitutes its bundled default).
function isValidField(field: unknown): field is QuestionnaireField {
    if (field === null || typeof field !== 'object') return false;
    const f = field as Record<string, unknown>;
    return typeof f.key === 'string' && f.key.length > 0 && typeof f.type === 'string';
}

function sanitiseSection(section: unknown): QuestionnaireSection | null {
    if (section === null || typeof section !== 'object') return null;
    const s = section as Record<string, unknown>;
    if (typeof s.key !== 'string' || !Array.isArray(s.fields)) return null;
    return {
        key: s.key,
        title: typeof s.title === 'string' ? s.title : undefined,
        description: typeof s.description === 'string' ? s.description : undefined,
        fields: s.fields.filter(isValidField),
    };
}

export function parseSchema(json: string | undefined | null): QuestionnaireSchema {
    if (!json) return EMPTY_SCHEMA;
    try {
        const parsed = JSON.parse(json);
        if (!parsed || !Array.isArray(parsed.sections)) return EMPTY_SCHEMA;
        const sections = parsed.sections
            .map(sanitiseSection)
            .filter((s: QuestionnaireSection | null): s is QuestionnaireSection => s !== null);
        if (sections.length === 0) return EMPTY_SCHEMA;
        return {
            version: typeof parsed.version === 'number' ? parsed.version : 1,
            sections,
        };
    } catch {
        return EMPTY_SCHEMA;
    }
}

// Serialise an Ant Design form's values into the {question, answer} pairs the
// API persists, driven generically by the schema. Mirrors the previous
// hand-written createHealthFormRequest exactly for the ported Southbank PARQ:
//   - checkboxGroup -> values joined with '|'
//   - boolean       -> "true" / "false"
//   - date          -> ISO string (dayjs/moment/Date), matching the old
//                      JSON.stringify behaviour of the raw picker value
//   - everything else -> the raw value as-is
// Display-only fields (info) contribute no answer.
export function schemaToAnswers(
    schema: QuestionnaireSchema,
    values: Record<string, unknown>,
): HealthFormAnswer[] {
    const answers: HealthFormAnswer[] = [];
    for (const section of schema.sections) {
        for (const field of section.fields) {
            if (NON_ANSWER_FIELD_TYPES.has(field.type)) continue;
            const raw = values[field.key];
            answers.push({ question: field.key, answer: serialiseAnswer(field.type, raw) });
        }
    }
    return answers;
}

function serialiseAnswer(type: string, raw: unknown): string {
    switch (type) {
        case 'checkboxGroup':
            return Array.isArray(raw) ? raw.join('|') : '';
        case 'boolean':
            return Boolean(raw).toString();
        case 'date':
            return serialiseDate(raw);
        default:
            return raw == null ? '' : String(raw);
    }
}

// dayjs and moment both expose toISOString(); a JS Date does too. Anything else
// falls back to String() so we never throw on an unexpected shape.
//
// A blank date is stamped with today: the only date field in our questionnaires
// is the declaration date at the bottom, which records *when the questionnaire
// was completed*. Rather than pre-filling the picker (and forcing the operator
// to notice/clear it), we default it to today at submit time if left empty.
function serialiseDate(raw: unknown): string {
    if (raw == null) return dayjs().startOf('day').toISOString();
    const maybe = raw as { toISOString?: () => string };
    if (typeof maybe.toISOString === 'function') return maybe.toISOString();
    return String(raw);
}

// Reverse of schemaToAnswers: hydrate an Ant Design form's values from stored
// {question, answer} pairs, so an existing submission can pre-fill the editor
// when reception updates a member's questionnaire. Inverts the serialisation
// (checkboxGroup split on '|', boolean "true"/"false", date -> dayjs).
export function answersToFormValues(
    schema: QuestionnaireSchema,
    answers: HealthFormAnswer[],
): Record<string, unknown> {
    const byKey = new Map(answers.map((a) => [a.question, a.answer]));
    const values: Record<string, unknown> = {};
    for (const section of schema.sections) {
        for (const field of section.fields) {
            if (NON_ANSWER_FIELD_TYPES.has(field.type)) continue;
            const raw = byKey.get(field.key);
            if (raw == null) continue;
            switch (field.type) {
                case 'checkboxGroup':
                    values[field.key] = raw ? raw.split('|').filter(Boolean) : [];
                    break;
                case 'boolean':
                    values[field.key] = raw === 'true';
                    break;
                case 'date':
                    values[field.key] = raw ? dayjs(raw) : undefined;
                    break;
                default:
                    values[field.key] = raw;
            }
        }
    }
    return values;
}
